import { Button } from '@/components/ui/shadcn/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/shadcn/form'
import { Input } from '@/components/ui/shadcn/input'
import { trpc } from '@/config/trpc'
import { useUserContext } from '@/context/userLogin'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useEffect } from 'react'
import { usePostHog } from 'posthog-js/react'

type Props = {
  email: string
  onEmailChange: (email: string) => void
  onSwitchToRegister: () => void
  onVerificationRequired: () => void
  onForgotPassword: () => void
}

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Please enter a valid email address' }),
  password: z.string().min(1, { message: 'Password is required' }),
})

export function LoginForm({
  email,
  onEmailChange,
  onSwitchToRegister,
  onVerificationRequired,
  onForgotPassword,
}: Props) {
  const { onAuthSuccess } = useUserContext()
  const posthog = usePostHog()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: email || '',
      password: '',
    },
  })

  const { mutateAsync: authorize, isPending } = trpc.auth.authorize.useMutation()

  const watchedEmail = form.watch('email') // eslint-disable-line react-hooks/incompatible-library -- React Hook Form API

  useEffect(() => {
    if (watchedEmail !== email) {
      onEmailChange(watchedEmail)
    }
  }, [watchedEmail, email, onEmailChange])

  const handleLogin = async (values: z.infer<typeof formSchema>) => {
    posthog.capture('auth_login_attempted', { provider: 'userpass' })
    try {
      const result = await authorize({
        provider: 'userpass',
        email: values.email,
        password: values.password,
      })

      if (!result.ok) {
        posthog.capture('auth_login_failed', {
          provider: 'userpass',
          reason: 'user_not_verified',
        })
        onVerificationRequired()
        return
      }

      onAuthSuccess(result.sessionToken, result.refreshToken, result.firstLogin)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An error occurred'
      const invalidCredentials = message === 'Invalid credentials'
      posthog.capture('auth_login_failed', {
        provider: 'userpass',
        reason: invalidCredentials ? 'invalid_credentials' : 'unknown',
      })
      // New users often try to log in before they have an account, and Google signups
      // have no password. Point at both without revealing whether the email exists.
      form.setError('root', {
        message: invalidCredentials
          ? 'Email or password is incorrect. New to CASSI? Sign up first. If you signed up with Google, use Login with Google.'
          : message,
      })
    }
  }

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleLogin)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username</FormLabel>
                <FormControl>
                  <Input size="lg" placeholder="Email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input size="lg" placeholder="Password" type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {form.formState.errors.root && (
            <div className="mt-1">
              <FormMessage>{form.formState.errors.root.message}</FormMessage>
            </div>
          )}

          <Button
            type="submit"
            disabled={isPending}
            className="mt-4 w-full"
            size="lg"
            variant="primary">
            {isPending ? (
              <span className="border-primary/30 border-t-primary inline-block h-4 w-4 animate-spin rounded-full border-2" />
            ) : (
              'Login'
            )}
          </Button>
        </form>
      </Form>

      <div className="mt-2 space-y-1">
        <p className="text-muted-foreground text-center text-xs">
          Don't have an account yet?{' '}
          <a
            className="text-primary cursor-pointer underline"
            onClick={(e) => {
              e.preventDefault()
              onSwitchToRegister()
            }}>
            Register
          </a>
          .
        </p>

        <p className="text-muted-foreground text-center text-xs">
          <a
            className="text-primary cursor-pointer underline"
            onClick={(e) => {
              e.preventDefault()
              onForgotPassword()
            }}>
            I forgot my password.
          </a>
        </p>
      </div>
    </div>
  )
}
